import * as React from 'react';
import { createContext, type FC, useRef, useState } from 'react';

import { useTranslation } from '@crehana/i18n';
// biome-ignore lint/style/noRestrictedImports: legacy import kept for compatibility
import { Snackbar } from '@crehana/ui';

import { LOCALES } from '../i18n';

interface DashboardContextProviderProps {
  isDark?: boolean;
}

export interface DashboardContextType {
  openErrorSnackbar: (message?: string) => void;
  sectionRef: React.RefObject<HTMLDivElement>;
  scrollTop: () => void;
}

export const defaultContext = {
  // biome-ignore lint/suspicious/noEmptyBlockStatements: intentionally empty block
  openErrorSnackbar: () => {},
  sectionRef: React.createRef<HTMLDivElement>(),
  // biome-ignore lint/suspicious/noEmptyBlockStatements: intentionally empty block
  scrollTop: () => {},
};

export const DashboardContext =
  createContext<DashboardContextType>(defaultContext);

const DashboardContextProvider: FC<
  React.PropsWithChildren<DashboardContextProviderProps>
> = ({ isDark, children }) => {
  const { languageCode } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const STRINGS = LOCALES[languageCode];

  const scrollTop = () => {
    if (sectionRef.current) {
      sectionRef.current.scroll({ top: 0, left: 0, behavior: 'smooth' });
    }
  };

  const [errorSnackbar, setErrorSnackbar] = useState<{
    isOpen: boolean;
    message: string;
  }>({ isOpen: false, message: STRINGS.GENERAL_ERROR });

  const openErrorSnackbar = (message?: string) => {
    setErrorSnackbar({
      isOpen: true,
      message: message || STRINGS.GENERAL_ERROR,
    });
  };

  const closeErrorSnackbar = () => {
    setErrorSnackbar(prevState => ({ ...prevState, isOpen: false }));
  };

  return (
    <DashboardContext.Provider
      value={{ openErrorSnackbar, sectionRef, scrollTop }}
    >
      {children}
      <Snackbar
        open={errorSnackbar.isOpen}
        onDismiss={closeErrorSnackbar}
        message={errorSnackbar.message}
        state="alert"
        position="top-right"
        showCloseBtn
        dark={isDark}
      />
    </DashboardContext.Provider>
  );
};

export default DashboardContextProvider;
